import { observer } from "mobx-react-lite";
import Btn from "../../ui/Btn";
import appStore from "../../storage/AppStore";
import {
  addMonths,
  endOfMonth,
  monthLabel,
  startOfMonth
} from "../../common/utils";
import Select from "../../ui/Select";
import { useEffect, useMemo, useState } from "react";

const normalizeToDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const getInitialMonth = (store) => {
  const qFrom = normalizeToDate(store?.competitionsQuery?.date_from);
  if (qFrom) return startOfMonth(qFrom);
  return startOfMonth(new Date());
};

const CompetitionsFilter = ({ store }) => {
  const [tournaments, setTournaments] = useState([]);
  const [month, setMonth] = useState(() => getInitialMonth(store));

  const isNextDisabled = useMemo(() => {
    const next = addMonths(month, 1);
    return startOfMonth(next) > startOfMonth(new Date());
  }, [month]);

  const applyMonthToQuery = (m, resetTournament = false) => {
    const from = startOfMonth(m);
    let to = endOfMonth(m);

    const now = new Date();

    const isCurrentMonth =
      from.getFullYear() === now.getFullYear() &&
      from.getMonth() === now.getMonth();

    if (isCurrentMonth) {
      to = null;
    }

    if (resetTournament) {
      store.competitionsQuery.setProperty("tournament_id", null);
    }

    store.competitionsQuery.setProperty("date_from", from);
    store.competitionsQuery.setProperty("date_to", to);
  };

  const syncMonthFromQuery = () => {
    const qFrom = normalizeToDate(store?.competitionsQuery?.date_from);
    const nextMonth = qFrom ? startOfMonth(qFrom) : startOfMonth(new Date());

    setMonth((prev) => {
      if (prev.getTime() === nextMonth.getTime()) return prev;
      return nextMonth;
    });
  };

  const loadFilterOptions = async () => {
    const tt = await store.searchTournamentsForFilter().catch(console.error);
    setTournaments(tt || []);
  };

  const goPrev = () => {
    const m = addMonths(month, -1);
    setMonth(m);
    applyMonthToQuery(m);
    store.getCompetitions();
  };

  const goNext = () => {
    if (isNextDisabled) return;

    const m = addMonths(month, 1);
    setMonth(m);
    applyMonthToQuery(m);
    store.getCompetitions();
  };

  const goCurrent = () => {
    const m = startOfMonth(new Date());
    setMonth(m);
    applyMonthToQuery(m);
    store.getCompetitions();
  };

  useEffect(() => {
    syncMonthFromQuery();

    const hasDateFrom = !!normalizeToDate(store?.competitionsQuery?.date_from);
    const hasDateTo =
      store?.competitionsQuery?.date_to === null ||
      !!normalizeToDate(store?.competitionsQuery?.date_to);

    if (!hasDateFrom && !hasDateTo) {
      const current = startOfMonth(new Date());
      setMonth(current);
      applyMonthToQuery(current);
    }

    loadFilterOptions();
  }, []);

  return (
    <div className="col-12 competition-filter">
      <div className="competition-filter__panel">
        <div className="competition-filter__month-row">
          <div className="competition-filter__calendar" aria-hidden="true">
            <i className="pi pi-calendar"/>
          </div>
          <div className="competition-filter__month-nav">
            <Btn icon="pi pi-angle-left" severity="secondary" outlined
                 className="competition-filter__arrow" onClick={goPrev}/>
            <Btn label={monthLabel(month, appStore.tr)} severity="secondary"
                 className="competition-filter__month-button" onClick={goCurrent}/>
            <Btn icon="pi pi-angle-right" severity="secondary" outlined
                 className="competition-filter__arrow" onClick={goNext} disabled={isNextDisabled}/>
          </div>
        </div>

        <div className="competition-filter__controls">
          <div className="competition-filter__select">
            <Select store={store.competitionsQuery} prop="tournament_id" options={tournaments}
                    id="input_competition_tournament" label={appStore.tr.tournament} for_search/>
          </div>
          <div className="competition-filter__action">
            <Btn icon="pi pi-search" label={appStore.tr.search}
                 className="competition-filter__search-button" onClick={() => {
                   store.competitionsQuery.setProperty("page_number", 1);
                   store.getCompetitions();
                 }}/>
          </div>
        </div>
      </div>
    </div>
  );
};

export default observer(CompetitionsFilter);